// Enregistrer / modifier un paiement contrat (1 doc = 1 mois ou 1 bloc multi-mois)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, Form, InputNumber, DatePicker, Input, Select, Button, Space, message, Typography } from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useAttachmentPreview } from '../../../contexts/AttachmentPreviewContext';
import type { Business } from '../../../types/business';
import type { BusinessContract, BusinessContractPayment } from '../../../types/businessContract';
import {
  applyContractPaymentDiscount,
  getContractPaymentDiscountInfo,
  getCoverageMonthCount,
} from '../../../types/businessContract';
import {
  useRecordContractPayment,
  useUpdateContractPayment,
} from '../../../hooks/useBusinessContracts';
import { uploadContractPaymentProof } from '../../../hooks/useExpenseAttachments';
import { formatNumber } from '../../../utils/numberFormat';
import dayjs, { Dayjs } from 'dayjs';

const newProofSessionId = () => `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function eachMonthInRange(start: Dayjs, end: Dayjs): Dayjs[] {
  const months: Dayjs[] = [];
  let m = start.startOf('month');
  const endM = end.startOf('month');
  if (m.isAfter(endM)) return [];
  while (m.isBefore(endM) || m.isSame(endM, 'month')) {
    months.push(m);
    m = m.add(1, 'month');
  }
  return months;
}

const MAX_BATCH_MONTHS = 36;

const { Text } = Typography;

const PAYMENT_TYPE_OPTIONS = [
  { value: 'monthly', label: 'Mensualité / bloc de mois' },
  { value: 'complimentary', label: 'Mois offert (0 €)' },
  { value: 'other', label: 'Autre frais' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente' },
  { value: 'paid', label: 'Payé' },
  { value: 'cancelled', label: 'Annulé' },
];

const FEE_LABEL_OPTIONS = [
  { value: "Frais d'installation", label: "Frais d'installation" },
  { value: 'Frais de formation', label: 'Frais de formation' },
  { value: 'Inventaire', label: 'Inventaire' },
  { value: 'Développements spécifiques', label: 'Développements spécifiques' },
];

const PROMO_LABEL_OPTIONS = [
  { value: 'Promotion', label: 'Promotion' },
  { value: 'Mois offert', label: 'Mois offert' },
  { value: 'Mois gratuit', label: 'Mois gratuit' },
  { value: 'Geste commercial', label: 'Geste commercial' },
  { value: 'Offre de bienvenue', label: 'Offre de bienvenue' },
];

interface RecordPaymentModalProps {
  open: boolean;
  business: Business;
  contract: BusinessContract | null;
  paymentToEdit?: BusinessContractPayment | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  open,
  business,
  contract,
  paymentToEdit = null,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!paymentToEdit;
  const [form] = Form.useForm();
  const recordMutation = useRecordContractPayment();
  const updateMutation = useUpdateContractPayment();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentType, setPaymentType] = useState<'monthly' | 'other' | 'complimentary'>('monthly');
  const coverageMonthRangeWatch = Form.useWatch('coverageMonthRange', form);
  const amountWatch = Form.useWatch('amount', form);
  const statusWatch = Form.useWatch('status', form);
  const discountModeWatch = Form.useWatch('discountMode', form) as 'none' | 'amount' | 'percent' | undefined;
  const discountValueWatch = Form.useWatch('discountValue', form);
  const showPaymentDate = paymentType === 'complimentary' || statusWatch === 'paid';

  const coverageMonthsCount = useMemo(() => {
    if (paymentType !== 'monthly' && paymentType !== 'complimentary') return 1;
    if (!coverageMonthRangeWatch?.[0] || !coverageMonthRangeWatch?.[1]) return 0;
    return eachMonthInRange(coverageMonthRangeWatch[0], coverageMonthRangeWatch[1]).length;
  }, [paymentType, coverageMonthRangeWatch]);

  const monthlyUnitAmount = typeof amountWatch === 'number' ? amountWatch : Number(amountWatch) || 0;
  const subtotalPreview =
    paymentType === 'complimentary'
      ? 0
      : paymentType === 'monthly' && coverageMonthsCount > 0
        ? monthlyUnitAmount * coverageMonthsCount
        : monthlyUnitAmount;

  const discountPreview = useMemo(
    () =>
      applyContractPaymentDiscount(
        subtotalPreview,
        discountModeWatch === 'amount' || discountModeWatch === 'percent' ? discountModeWatch : 'none',
        typeof discountValueWatch === 'number' ? discountValueWatch : Number(discountValueWatch) || 0
      ),
    [subtotalPreview, discountModeWatch, discountValueWatch]
  );

  const totalAmountPreview = discountPreview.amount;

  const [proofSessionId, setProofSessionId] = useState(newProofSessionId);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openAttachmentPreview = useAttachmentPreview();
  const isTabletOrMobile = useMediaQuery('(max-width: 1024px)');
  const drawerWidth = isTabletOrMobile ? '100%' : 480;

  useEffect(() => {
    if (!open) return;

    form.resetFields();
    setProofSessionId(newProofSessionId());

    if (paymentToEdit) {
      const type = paymentToEdit.paymentType;
      setPaymentType(type);
      const start = paymentToEdit.coverageStartDate
        ? dayjs(paymentToEdit.coverageStartDate).startOf('month')
        : dayjs().startOf('month');
      const end = paymentToEdit.coverageEndDate
        ? dayjs(paymentToEdit.coverageEndDate).startOf('month')
        : start;
      const monthCount = getCoverageMonthCount(paymentToEdit) || 1;
      const discountInfo = getContractPaymentDiscountInfo(paymentToEdit);
      const baseTotal = discountInfo.hasDiscount
        ? discountInfo.originalAmount
        : (paymentToEdit.amount ?? 0);
      const unitAmount =
        type === 'complimentary'
          ? 0
          : type === 'monthly' && monthCount > 1
            ? Math.round(baseTotal / monthCount)
            : baseTotal;

      let discountMode: 'none' | 'amount' | 'percent' = 'none';
      let discountValue: number | undefined;
      if (discountInfo.hasDiscount) {
        if (paymentToEdit.discountPercent && paymentToEdit.discountPercent > 0) {
          discountMode = 'percent';
          discountValue = paymentToEdit.discountPercent;
        } else {
          discountMode = 'amount';
          discountValue = discountInfo.discountAmount;
        }
      }

      form.setFieldsValue({
        paymentType: type,
        status: paymentToEdit.status,
        paymentDate: paymentToEdit.paymentDate ? dayjs(paymentToEdit.paymentDate) : dayjs(),
        amount: unitAmount,
        discountMode,
        discountValue,
        discountLabel: paymentToEdit.discountLabel ?? undefined,
        feeLabel: type === 'other' ? paymentToEdit.feeLabel : undefined,
        promoLabel: type === 'complimentary' ? paymentToEdit.feeLabel : undefined,
        coverageMonthRange: [start, end],
        reference: paymentToEdit.reference ?? '',
        notes: paymentToEdit.notes ?? '',
      });
      setAttachmentUrl(paymentToEdit.attachmentUrl ?? null);
      return;
    }

    setAttachmentUrl(null);
    const today = dayjs();
    form.setFieldsValue({
      paymentType: 'monthly',
      status: 'pending',
      paymentDate: today,
      coverageMonthRange: [today.startOf('month'), today.startOf('month')],
      amount: contract?.monthlyAmount,
      discountMode: 'none',
      discountValue: undefined,
      discountLabel: undefined,
      feeLabel: undefined,
      promoLabel: undefined,
      reference: '',
      notes: '',
    });
    setPaymentType('monthly');
  }, [open, form, paymentToEdit, contract?.monthlyAmount]);

  const handleProofFile = async (file: File) => {
    if (!business?.id) return;
    setUploadingProof(true);
    try {
      const storageKey =
        isEditMode && paymentToEdit ? paymentToEdit.id : `${proofSessionId}-proof`;
      const url = await uploadContractPaymentProof(business.id, storageKey, file);
      setAttachmentUrl(url);
      message.success('Justificatif envoyé');
    } catch {
      message.error("Erreur lors de l'envoi du justificatif");
    } finally {
      setUploadingProof(false);
    }
  };

  const handlePaymentTypeChange = (value: 'monthly' | 'other' | 'complimentary') => {
    if (isEditMode) return;
    setPaymentType(value);
    const today = dayjs();
    if (value === 'complimentary') {
      form.setFieldsValue({
        amount: 0,
        status: 'paid',
        paymentDate: today,
        coverageMonthRange: [today.startOf('month'), today.startOf('month')],
      });
    } else if (value === 'monthly') {
      form.setFieldsValue({
        amount: contract?.monthlyAmount,
        status: 'pending',
        coverageMonthRange: [today.startOf('month'), today.startOf('month')],
      });
    } else {
      form.setFieldsValue({ status: 'pending' });
    }
  };

  const resolveCoverageFromRange = (range: [Dayjs, Dayjs] | undefined) => {
    if (!range?.[0] || !range?.[1]) {
      return { error: 'Choisissez la plage de mois couverts' as const };
    }
    const months = eachMonthInRange(range[0], range[1]);
    if (months.length === 0) {
      return { error: 'Plage de mois invalide' as const };
    }
    if (months.length > MAX_BATCH_MONTHS) {
      return { error: `Maximum ${MAX_BATCH_MONTHS} mois par enregistrement. Réduisez la plage.` as const };
    }
    return {
      months,
      coverageStartDate: months[0].startOf('month').toDate(),
      coverageEndDate: months[months.length - 1].endOf('month').toDate(),
    };
  };

  const handleSubmit = async () => {
    if (!contract) {
      message.error('Un contrat doit exister pour enregistrer un paiement');
      return;
    }
    if (isEditMode && paymentToEdit?.status === 'paid') {
      message.error('Un paiement déjà payé ne peut pas être modifié');
      return;
    }
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const hasCoverage = paymentType === 'monthly' || paymentType === 'complimentary';
      let coverageStartDate: Date | null = null;
      let coverageEndDate: Date | null = null;
      let monthCount = 1;

      if (hasCoverage) {
        const resolved = resolveCoverageFromRange(values.coverageMonthRange as [Dayjs, Dayjs] | undefined);
        if ('error' in resolved) {
          message.error(resolved.error);
          setIsSubmitting(false);
          return;
        }
        coverageStartDate = resolved.coverageStartDate;
        coverageEndDate = resolved.coverageEndDate;
        monthCount = resolved.months.length;
      }

      const unitAmount = paymentType === 'complimentary' ? 0 : (values.amount ?? 0);
      const subtotal =
        paymentType === 'complimentary'
          ? 0
          : paymentType === 'monthly'
            ? unitAmount * monthCount
            : unitAmount;

      const discountMode =
        values.discountMode === 'amount' || values.discountMode === 'percent'
          ? values.discountMode
          : 'none';
      const discounted = applyContractPaymentDiscount(
        subtotal,
        paymentType === 'complimentary' ? 'none' : discountMode,
        values.discountValue ?? 0
      );
      const totalAmount = discounted.amount;
      const originalAmount = discounted.discountAmount > 0 ? discounted.originalAmount : null;
      const discountPercent = discounted.discountPercent;
      const discountLabel =
        discounted.discountAmount > 0
          ? (values.discountLabel?.trim() || 'Remise commerciale')
          : null;

      const isFullyDiscounted =
        paymentType !== 'complimentary' && totalAmount === 0 && discounted.discountAmount > 0;

      let status: 'pending' | 'paid' | 'cancelled' =
        paymentType === 'complimentary' ? 'paid' : values.status;
      // Remise 100 % : rien à encaisser → on clôture l’échéance comme réglée
      if (isFullyDiscounted) {
        status = 'paid';
      }

      if (status === 'paid' && !isFullyDiscounted && !values.paymentDate) {
        message.error('Indiquez la date de paiement');
        setIsSubmitting(false);
        return;
      }

      const feeLabel =
        paymentType === 'complimentary'
          ? values.promoLabel || 'Mois offert'
          : paymentType === 'other'
            ? values.feeLabel || 'Autres frais'
            : null;

      const payloadBase = {
        businessId: business.id,
        paymentDate:
          status === 'paid'
            ? values.paymentDate && typeof values.paymentDate.toDate === 'function'
              ? values.paymentDate.toDate()
              : new Date()
            : null,
        amount: totalAmount,
        originalAmount,
        discountLabel,
        discountPercent,
        paymentType,
        status,
        coverageStartDate,
        coverageEndDate,
        feeLabel,
        reference: values.reference || null,
        notes: values.notes || null,
        attachmentUrl: attachmentUrl || null,
      };

      if (isEditMode && paymentToEdit) {
        await updateMutation.mutateAsync({
          paymentId: paymentToEdit.id,
          ...payloadBase,
        });
        message.success(
          isFullyDiscounted
            ? 'Échéance offerte (remise 100 %) — marquée comme réglée'
            : status === 'paid'
              ? 'Paiement enregistré'
              : 'Échéance mise à jour'
        );
      } else {
        await recordMutation.mutateAsync({
          ...payloadBase,
          reference: values.reference || undefined,
          notes: values.notes || undefined,
          attachmentUrl: attachmentUrl || undefined,
          feeLabel: feeLabel || undefined,
          coverageStartDate: coverageStartDate || undefined,
          coverageEndDate: coverageEndDate || undefined,
        });
        message.success(
          isFullyDiscounted
            ? monthCount > 1
              ? `Échéance groupée offerte (remise 100 %, ${monthCount} mois).`
              : 'Échéance offerte (remise 100 %) — marquée comme réglée'
            : status === 'paid'
              ? monthCount > 1
                ? `Paiement groupé enregistré (${monthCount} mois, total ${formatNumber(totalAmount, 0)} FCFA).`
                : 'Paiement enregistré avec succès'
              : monthCount > 1
                ? `Échéance groupée créée (${monthCount} mois, total ${formatNumber(totalAmount, 0)} FCFA).`
                : 'Échéance créée avec succès'
        );
      }

      form.resetFields();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      const msg = err instanceof Error ? err.message : null;
      message.error(
        msg ||
          (isEditMode
            ? 'Erreur lors de la modification'
            : "Erreur lors de l'enregistrement du paiement")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAttachmentUrl(null);
    onClose();
  };

  const submitLabel = (() => {
    if (isEditMode) {
      return statusWatch === 'paid' ? 'Enregistrer le paiement' : 'Enregistrer l’échéance';
    }
    if (statusWatch === 'paid') {
      return paymentType === 'monthly' && coverageMonthsCount > 1
        ? `Enregistrer le paiement (${coverageMonthsCount} mois)`
        : 'Enregistrer le paiement';
    }
    if (paymentType === 'monthly' && coverageMonthsCount > 1) {
      return `Créer l’échéance (${coverageMonthsCount} mois)`;
    }
    return 'Créer l’échéance';
  })();

  return (
    <Drawer
      title={
        isEditMode
          ? statusWatch === 'paid'
            ? 'Enregistrer le paiement'
            : 'Modifier l’échéance'
          : statusWatch === 'paid'
            ? 'Enregistrer un paiement'
            : 'Créer une échéance'
      }
      open={open}
      onClose={handleCancel}
      width={drawerWidth}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleCancel}>Annuler</Button>
          <Button type="primary" loading={isSubmitting} onClick={handleSubmit}>
            {submitLabel}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="paymentType" label="Type" rules={[{ required: true }]}>
          <Select
            options={PAYMENT_TYPE_OPTIONS}
            onChange={handlePaymentTypeChange}
            disabled={isEditMode}
          />
        </Form.Item>
        <Form.Item
          name="status"
          label="Statut"
          rules={[{ required: true, message: 'Choisissez le statut' }]}
          extra={
            statusWatch === 'pending'
              ? 'Échéance à régler par le client (pas encore de date de paiement).'
              : statusWatch === 'paid'
                ? 'Règlement effectué : la date de paiement est obligatoire.'
                : undefined
          }
        >
          <Select
            options={PAYMENT_STATUS_OPTIONS}
            disabled={paymentType === 'complimentary'}
            onChange={(value) => {
              if (value === 'paid' && !form.getFieldValue('paymentDate')) {
                form.setFieldsValue({ paymentDate: dayjs() });
              }
            }}
          />
        </Form.Item>
        {showPaymentDate && (
          <Form.Item
            name="paymentDate"
            label="Date du paiement"
            rules={[{ required: true, message: 'Choisissez la date du paiement' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        )}
        {paymentType !== 'complimentary' && (
          <>
            <Form.Item
              name="amount"
              label={paymentType === 'monthly' ? 'Montant mensuel (FCFA)' : 'Montant (FCFA)'}
              extra={
                paymentType === 'monthly' && coverageMonthsCount > 0 ? (
                  <Text type="secondary">
                    Sous-total {coverageMonthsCount} mois : {formatNumber(subtotalPreview, 0)} FCFA
                  </Text>
                ) : undefined
              }
              rules={[{ required: true, message: 'Saisissez le montant' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1000}
                formatter={(v) => String(v ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                addonAfter="FCFA"
              />
            </Form.Item>
            <Form.Item name="discountMode" label="Remise" initialValue="none">
              <Select
                options={[
                  { value: 'none', label: 'Aucune remise' },
                  { value: 'percent', label: 'Pourcentage (%)' },
                  { value: 'amount', label: 'Montant fixe (FCFA)' },
                ]}
                onChange={(mode) => {
                  if (mode === 'none') {
                    form.setFieldsValue({ discountValue: undefined, discountLabel: undefined });
                  }
                }}
              />
            </Form.Item>
            {(discountModeWatch === 'percent' || discountModeWatch === 'amount') && (
              <>
                <Form.Item
                  name="discountValue"
                  label={discountModeWatch === 'percent' ? 'Valeur de la remise (%)' : 'Montant de la remise (FCFA)'}
                  rules={[{ required: true, message: 'Indiquez la remise' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={discountModeWatch === 'percent' ? 100 : undefined}
                    step={discountModeWatch === 'percent' ? 1 : 1000}
                    addonAfter={discountModeWatch === 'percent' ? '%' : 'FCFA'}
                  />
                </Form.Item>
                <Form.Item
                  name="discountLabel"
                  label="Libellé de la remise (visible client)"
                  extra="Ex. Remise bloc 6 mois, Promo rentrée…"
                >
                  <Input placeholder="Remise commerciale" maxLength={80} />
                </Form.Item>
              </>
            )}
            {discountPreview.discountAmount > 0 && (
              <div
                style={{
                  marginBottom: 16,
                  padding: '10px 12px',
                  background: discountPreview.amount === 0 ? '#f6ffed' : '#fff7e6',
                  border: `1px solid ${discountPreview.amount === 0 ? '#b7eb8f' : '#ffd591'}`,
                  borderRadius: 6,
                }}
              >
                <Text delete type="secondary">
                  {formatNumber(discountPreview.originalAmount, 0)} FCFA
                </Text>
                {' → '}
                <Text strong style={{ color: discountPreview.amount === 0 ? '#389e0d' : '#d46b08' }}>
                  {formatNumber(discountPreview.amount, 0)} FCFA
                </Text>
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                  Économie : {formatNumber(discountPreview.discountAmount, 0)} FCFA
                  {discountPreview.discountPercent != null
                    ? ` (−${discountPreview.discountPercent} %)`
                    : ''}
                  {discountPreview.amount === 0
                    ? ' · Remise totale : l’échéance sera marquée comme réglée (offerte).'
                    : ''}
                </Text>
              </div>
            )}
          </>
        )}
        {paymentType === 'complimentary' && (
          <Form.Item
            name="promoLabel"
            label="Motif du mois offert"
            rules={[{ required: true, message: 'Choisissez ou saisissez le motif' }]}
          >
            <Select
              placeholder="Ex: Promotion, Mois gratuit..."
              allowClear
              options={PROMO_LABEL_OPTIONS}
            />
          </Form.Item>
        )}
        {paymentType === 'other' && (
          <Form.Item name="feeLabel" label="Libellé du frais">
            <Select
              placeholder="Ex: Frais d'installation"
              allowClear
              options={FEE_LABEL_OPTIONS}
            />
          </Form.Item>
        )}
        {(paymentType === 'monthly' || paymentType === 'complimentary') && (
          <Form.Item
            name="coverageMonthRange"
            label="Mois couverts"
            extra="Intervalle inclusif : un seul enregistrement couvre toute la plage (ex. 3, 4 ou 6 mois)."
            rules={[
              { required: true, message: 'Choisissez la plage de mois' },
              {
                validator: (_, value) => {
                  if (!value || !Array.isArray(value) || value.length !== 2 || !value[0] || !value[1]) {
                    return Promise.reject(new Error('Sélectionnez le premier et le dernier mois'));
                  }
                  const months = eachMonthInRange(value[0], value[1]);
                  if (months.length === 0) {
                    return Promise.reject(new Error('Le premier mois doit précéder ou égaler le dernier'));
                  }
                  if (months.length > MAX_BATCH_MONTHS) {
                    return Promise.reject(
                      new Error(`Maximum ${MAX_BATCH_MONTHS} mois par enregistrement`)
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker.RangePicker
              picker="month"
              style={{ width: '100%' }}
              format="MM/YYYY"
              placeholder={['Premier mois', 'Dernier mois']}
            />
          </Form.Item>
        )}
        <Form.Item name="reference" label="Référence (n° virement, reçu, etc.)">
          <Input placeholder="Ex: Virement ref. 12345" />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Commentaire optionnel" />
        </Form.Item>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          aria-label="Joindre un justificatif de paiement"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleProofFile(f);
            e.target.value = '';
          }}
        />
        {paymentType !== 'complimentary' && (
          <Form.Item label="Justificatif de paiement (optionnel)">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Button
                icon={<PaperClipOutlined />}
                onClick={() => fileInputRef.current?.click()}
                loading={uploadingProof}
              >
                Joindre un justificatif
              </Button>
              {attachmentUrl && (
                <Space>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => openAttachmentPreview?.(attachmentUrl)}
                    style={{ padding: 0 }}
                  >
                    Voir le fichier
                  </Button>
                  <Button type="link" size="small" danger onClick={() => setAttachmentUrl(null)}>
                    Retirer
                  </Button>
                </Space>
              )}
            </div>
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
};

export default RecordPaymentModal;
